import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _ from 'underscore';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { ServerUrls } from '~commonInfra/url/ServerUrls';
import { MapDispatchToProps, MapStateToProps } from '~commonInfra/utils/ConnectUtils';
import { SessionUserSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { updateBreadcrumbs } from '~src/spacesPlatform/actions/UpdateBreadcrumbsAction';
import { BreadcrumbStateInfo } from '~src/spacesPlatform/components/breadcrumbs/BreadcrumbStateInfo';
import { ConnectedSpaceInfoProvider } from '~src/spacesPlatform/components/Explorer/helpers/SpaceInfoProvider';
import { SpaceInfo } from '~src/spacesPlatform/state/SpaceInfo';
import { ServerUsersWithTokenResource } from '~src/userManagement/services/resources/ServerUsersWithToken';

import { RouterError } from '../../../../bootstrap/routerHelpers/RouterError';
import { Messages } from '../../../../codegen/Localize';
import { ResultHelper } from '../../../../httpClient/utils/ResultHelper';
import { IGetUserSettingsResult } from '../../../../userSettings/serverApi/requests/GetUserSettingsRequest';
import { UserSettingsActions } from '../../../../userSettings/services/UserSettingsActions';
import { ServerUsers } from '../../../services/resources/ServerUsers';
import { SiteRoleOptionsResource } from '../../../services/resources/SiteRoleOptions';
import { IServerUserInfo } from '../../models/IServerUserInfo';
import { ServerUserSpace, ServerUserSpaceProps } from './ServerUserSpace';

export function getServerUsersPlaceBreadcrumb(): BreadcrumbStateInfo {
  const serverUrls = ServerUrls.getInstance();
  return {
    id: serverUrls.usersUrl(),
    text: Messages.allServerUsers(),
    href: serverUrls.usersUrl(),
    testId: 'server-users-link'
  };
}

export interface ServerUserSpaceProviderOwnProps {
  readonly userName: string;
  readonly domainName: string;
  readonly renderSpace: (props: ServerUserSpaceProps) => JSX.Element;
}

export interface ServerUserSpaceProviderStateProps {
  readonly isSessionUserSelf: boolean;
}

export interface ServerUserSpaceProviderDispatchProps {
  readonly updateBreadcrumbs: (breadcrumbs: ReadonlyArray<BreadcrumbStateInfo>) => void;
}

export type ServerUserSpaceProviderComponentProps = ServerUserSpaceProviderOwnProps & ServerUserSpaceProviderStateProps & ServerUserSpaceProviderDispatchProps;

export interface ServerUserSpaceProviderState {
  readonly initialRender: boolean;
  readonly renderChildren: boolean;
  readonly hasError: boolean;
  readonly serverUser: IServerUserInfo;
  readonly userSettings: IGetUserSettingsResult;
  readonly userRefreshTokenCount: number;
}

export class ServerUserSpaceProviderComponent extends React.PureComponent<ServerUserSpaceProviderComponentProps, ServerUserSpaceProviderState> {
  private unmounted: boolean = false;
  private readonly initialState: ServerUserSpaceProviderState = {
    initialRender: true,
    renderChildren: false,
    hasError: false,
    serverUser: undefined,
    userSettings: undefined,
    userRefreshTokenCount: undefined
  };

  public constructor(props: ServerUserSpaceProviderComponentProps) {
    super(props);
    this.state = this.initialState;
  }

  public render() {
    if (this.state.renderChildren) {
      return this.props.renderSpace({
        user: this.state.serverUser,
        userSettings: this.state.userSettings,
        userRefreshTokenCount: this.state.userRefreshTokenCount,
        siteRoleOptions: SiteRoleOptionsResource.instance,
        isSessionUserSelf: this.props.isSessionUserSelf,
      });
    } else if (this.state.hasError) {
      return <RouterError errorType={'serveruser'} urls={undefined} />;
    } else {
      return null;
    }
  }

  public componentDidMount() {
    this.getData();
  }

  public componentDidUpdate(prevProps: ServerUserSpaceProviderComponentProps) {
    if (this.userChanged(prevProps)) {
      this.updateState(this.initialState);
      this.getData();
      return;
    }

    // if we dont have all we need - do nothing
    if (!this.haveAllDataNeededToRender()) {
      return;
    }

    if (this.state.initialRender) {
      this.updateBreadcrumbs();
      this.setState({initialRender: false, renderChildren: true});
    }
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  private updateBreadcrumbs() {
    const breadcrumbs: BreadcrumbStateInfo[] =  [
      getServerUsersPlaceBreadcrumb(),
      { text: this.state.serverUser.displayName }
    ];

    this.props.updateBreadcrumbs(breadcrumbs);
  }

  private haveAllDataNeededToRender() {
    return !_.isUndefined(this.state.serverUser) && !_.isUndefined(this.state.userSettings)
      && !_.isUndefined(this.state.userRefreshTokenCount);
  }

  private userChanged(prevProps: ServerUserSpaceProviderComponentProps): boolean {
    return (!_.isUndefined(prevProps.userName)
      && !_.isUndefined(this.props.userName)
      && prevProps.userName !== this.props.userName) ||
      (!_.isUndefined(prevProps.domainName)
      && !_.isUndefined(this.props.domainName)
      && prevProps.domainName !== this.props.domainName);
  }

  private getData = async () => {
    this.getUser();
    this.getUserSettings();
    this.getUserRefreshTokenCount();
  };

  private updateState<K extends keyof ServerUserSpaceProviderState>(newState: Pick<ServerUserSpaceProviderState, K>, callback?: () => void): void {
    if (!this.unmounted) {
      this.setState(newState, callback);
    }
  }

  private getUser = async () => {
    try {
      const serverUser = FeatureFlags.serverUserSolrMigrationEnabled ?
        await ServerUsersWithTokenResource.getUserByUsernameAndDomain(this.props.userName, this.props.domainName) :
        await ServerUsers.instance.getUserByUsernameAndDomain(this.props.userName, this.props.domainName);
      if (serverUser) {
        this.updateState({serverUser});
      } else {
        this.updateState({hasError: true});
      }
    } catch (e) {
      this.updateState({hasError: true});
    }
  };

  private getUserRefreshTokenCount = async () => {
    try {
      const userRefreshTokenCount = await ServerUsers.instance.getUserRefreshTokenCount(this.props.userName, this.props.domainName);
      this.updateState({userRefreshTokenCount});
    } catch (e) {
      this.updateState({hasError: true});
    }
  };

  private getUserSettings = async () => {
    try {
      const userSettings = await UserSettingsActions.fetchUserSettings(this.props.userName, this.props.domainName);
      if (ResultHelper.hasErrors(userSettings)) {
        this.updateState({hasError: true});
      } else {
        this.updateState({userSettings});
      }
    } catch (e) {
      this.updateState({hasError: true});
    }
  };
}

const mapStateToProps: MapStateToProps<ServerUserSpaceProviderStateProps, ServerUserSpaceProviderOwnProps> = (appState, ownProps) => {
  return {
    isSessionUserSelf: SessionUserSelectors.sessionUserName(appState) === ownProps.userName && SessionUserSelectors.sessionDomainName(appState) === ownProps.domainName,
  };
};

const mapDispatchToProps: MapDispatchToProps<ServerUserSpaceProviderDispatchProps> = (dispatch) => bindActionCreators({
  updateBreadcrumbs
}, dispatch);

export const ConnectedServerUserSpaceProviderComponent: React.ComponentClass<ServerUserSpaceProviderOwnProps>
  = connect(mapStateToProps, mapDispatchToProps)(ServerUserSpaceProviderComponent);

const renderSpace = (props: ServerUserSpaceProps): JSX.Element => <ServerUserSpace {...props} />;

export type ServerUserSpaceProviderProps = Omit<ServerUserSpaceProviderOwnProps, 'renderSpace'>;

export function ServerUserSpaceProvider(props: ServerUserSpaceProviderProps) {
  const spaceInfo: SpaceInfo = {
    item: null,
    spaceType: 'serveruser',
    subspaceName: undefined
  };

  const componentProps: ServerUserSpaceProviderOwnProps = {
    domainName: props.domainName,
    userName: props.userName,
    renderSpace
  };

  return (
    <ConnectedSpaceInfoProvider spaceInfo={spaceInfo}>
      <ConnectedServerUserSpaceProviderComponent {...componentProps}/>
    </ConnectedSpaceInfoProvider>
  );
}
